"use client"

import React, { useCallback, useEffect, useId, useState } from "react"
import { AlertCircle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { FieldView, TrailStep } from "@/lib/review/finding-fields"

type FieldsResponse = { submissionId?: number; formId?: number; fields: FieldView[]; reason?: string }

/**
 * The submission answers behind one review finding, editable in place
 * (GET/PATCH /api/report-review/findings/{id}/fields). Loaded on demand — a
 * report can carry 40 findings and most authors open a handful.
 */
export function FindingFields({ findingId, onSaved }: { findingId: string; onSaved?: () => void }) {
  const [data, setData] = useState<FieldsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    fetch(`/api/report-review/findings/${findingId}/fields`, { cache: "no-store" })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}))
        if (!alive) return
        if (!r.ok) setError(body?.error || "Could not load the fields")
        else setData(body as FieldsResponse)
      })
      .catch(() => alive && setError("Could not load the fields"))
    return () => {
      alive = false
    }
  }, [findingId])

  if (error) {
    return (
      <p role="alert" className="mt-2 flex items-center gap-1 text-xs text-destructive">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {error}
      </p>
    )
  }
  if (!data) {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading fields…
      </p>
    )
  }
  if (data.fields.length === 0) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        No field on the current form matches this finding — it may be about the report&rsquo;s wording, or
        about answers from an older version of the form.
      </p>
    )
  }

  const editHref =
    data.formId && data.submissionId
      ? `/operations/submissions/${data.formId}/submission/${data.submissionId}/edit?back=${encodeURIComponent(
          typeof window !== "undefined" ? window.location.pathname + window.location.search : "/",
        )}`
      : null

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-border/60 bg-muted/30 p-2">
      {data.fields.map((field) => (
        <FieldEditorRow
          key={`${field.elementId}|${field.trail.map((t) => `${t.repeatId}:${t.rowIndex}`).join("/")}`}
          findingId={findingId}
          field={field}
          onSaved={onSaved}
        />
      ))}
      {editHref && (
        <a
          href={editHref}
          className="inline-flex items-center gap-1 self-start text-xs text-foreground underline underline-offset-2"
        >
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          Open the submission in the form
        </a>
      )}
    </div>
  )
}

function FieldEditorRow({
  findingId,
  field: initial,
  onSaved,
}: {
  findingId: string
  field: FieldView
  onSaved?: () => void
}) {
  const id = useId()
  const [field, setField] = useState(initial)
  const [text, setText] = useState(initial.value)
  const [choice, setChoice] = useState<string[]>(initial.values)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const dirty =
    field.editor === "choice"
      ? JSON.stringify([...choice].sort()) !== JSON.stringify([...field.values].sort())
      : text !== field.value

  const save = useCallback(async () => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const res = await fetch(`/api/report-review/findings/${findingId}/fields`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          elementId: field.elementId,
          trail: field.trail satisfies TrailStep[],
          value: field.editor === "choice" ? choice : text,
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(body?.error || "Could not save")
        return
      }
      const next = body.field as FieldView
      setField(next)
      setText(next.value)
      setChoice(next.values)
      setSaved(true)
      onSaved?.()
    } catch {
      setError("Could not save")
    } finally {
      setSaving(false)
    }
  }, [findingId, field, choice, text, onSaved])

  const optionLabel = (v: string) => field.options.find((o) => o.value === v)?.label ?? v
  // An answer the options no longer include (renamed or removed) stays visible
  // rather than silently disappearing from the control.
  const orphanValues = field.editor === "choice" ? field.values.filter((v) => !field.options.some((o) => o.value === v)) : []

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={field.editor === "choice" && field.multiple ? undefined : id} className="text-xs">
        <span className="font-medium text-foreground">{field.label}</span>
        {field.context && <span className="text-muted-foreground"> · {field.context}</span>}
      </label>

      {field.editor === "readonly" && (
        <p className="text-xs text-muted-foreground">
          <span className="text-foreground">{field.value || "(blank)"}</span> — this kind of field can only be
          changed in the form.
        </p>
      )}

      {field.editor === "text" && (
        <Input id={id} value={text} onChange={(e) => setText(e.target.value)} className="h-8 text-sm" />
      )}

      {field.editor === "textarea" && (
        <Textarea id={id} value={text} onChange={(e) => setText(e.target.value)} rows={3} className="text-sm" />
      )}

      {field.editor === "choice" && !field.multiple && (
        <select
          id={id}
          value={choice[0] ?? ""}
          onChange={(e) => setChoice(e.target.value ? [e.target.value] : [])}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">(blank)</option>
          {[...orphanValues.map((v) => ({ label: `${v} (no longer an option)`, value: v })), ...field.options].map(
            (o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ),
          )}
        </select>
      )}

      {field.editor === "choice" && field.multiple && (
        <fieldset className="flex flex-col gap-1">
          <legend className="sr-only">{field.label}</legend>
          {[...orphanValues.map((v) => ({ label: `${v} (no longer an option)`, value: v })), ...field.options].map(
            (o, i) => (
              <label key={o.value} className="flex items-center gap-2 text-sm" htmlFor={`${id}-${i}`}>
                <input
                  id={`${id}-${i}`}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={choice.includes(o.value)}
                  onChange={(e) =>
                    setChoice((prev) => (e.target.checked ? [...prev, o.value] : prev.filter((v) => v !== o.value)))
                  }
                />
                {o.label}
              </label>
            ),
          )}
        </fieldset>
      )}

      {field.editor !== "readonly" && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!dirty || saving} onClick={save}>
            {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />}
            Save
          </Button>
          {saved && !dirty && (
            <span role="status" className="flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
              Saved{field.editor === "choice" ? ` as ${field.values.map(optionLabel).join(", ") || "blank"}` : ""} —
              re-generate the report to update it.
            </span>
          )}
          {error && (
            <span role="alert" className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
